import { createContext, useContext } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import type { SurfaceDef } from './migration'
import { HomeTopology } from '../flowmap/HomeTopology'
import { EASE_DECEL, useTierDurations } from '../motion/transitions'

/**
 * Provided by the U3 WindowFrame so surfaces can adapt to windowed
 * mounts. Default false: everything is on the floor until the
 * workspace layer lands.
 */
export const InWindowContext = createContext(false)

export function setV1Cookie() {
  document.cookie = 'hubv=1; path=/; max-age=31536000; SameSite=Lax'
}

/**
 * The unmigrated-route surface (R22). V1-styled pages never render
 * inside the V2 shell — a route whose surface isn't live yet gets this
 * instead, with a one-click path back to V1 at the same URL. Inside a
 * window it opens V1 in a new tab so the workspace layout survives.
 */
export function Interstitial({ surface }: { surface: SurfaceDef }) {
  const inWindow = useContext(InWindowContext)
  const { pathname } = useLocation()
  const dur = useTierDurations()
  const target = surface.v1Path ?? pathname
  const building = surface.status === 'building'

  const openInV1 = () => {
    setV1Cookie()
    if (inWindow) {
      window.open(target, '_blank', 'noopener')
    } else {
      window.location.href = target
    }
  }

  return (
    <motion.section
      className="v2-interstitial"
      initial={{ opacity: 0, y: 14, filter: 'blur(6px)' }}
      animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      transition={{ duration: dur.page, ease: EASE_DECEL }}
      aria-live="polite"
    >
      <div className="v2-interstitial-panel fui-panel">
        <span className="fui-eyebrow">
          <span className="fui-eyebrow-dot" aria-hidden="true" />
          {surface.label} · {building ? 'in build' : 'coming in v2'}
        </span>
        <h1 className="v2-interstitial-title">
          {building ? 'This surface is being built right now.' : 'Coming in V2.'}
        </h1>
        <p className="v2-interstitial-sub">
          {building
            ? `${surface.label} is a V2 proof surface — it lands first. Until then, open it in V1 to keep working.`
            : `${surface.label} ships in a later wave. Open it in V1 to keep working — same data, classic skin.`}
        </p>
        <div className="v2-interstitial-actions">
          <button type="button" className="v2-btn v2-btn-primary" onClick={openInV1}>
            Open in V1{inWindow ? ' (new tab)' : ''}
          </button>
          <Link to="/" className="v2-btn">
            Back to Hub
          </Link>
        </div>
        <p className="v2-interstitial-note">
          V1 stays one click away — the appbar switch or <a href="/switch">/switch</a>.
        </p>
      </div>
      {/* Home teaser: the live topology breathes here until U7 gives it
          its bento cell — and it makes U5 verifiable in production. */}
      {surface.id === 'home' && !inWindow && <HomeTopology />}
    </motion.section>
  )
}
